import { createSlice, createAction } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import { isAxiosError } from "axios";
import axios, { getData } from "../../../axios";
import keyBy from "lodash/keyBy";
import { AppThunkAction, RootState } from "../../../redux-store";
import {
  createErrorReportingAsyncThunk,
  ServerRejection,
  serverRejectionMessage,
} from "../../helpers";
import { selectFeature } from "../../features/selectors";
import {
  Client,
  dispatchingClientT,
  dispatchingClientsT,
  addEconomicClientT,
  addTripletexClientT,
  addBusinessCentralClientT,
} from "./types";
import {
  AddEconomicClient,
  AddTripletexClient,
  AddBusinessCentralClient,
  customerGroupsT,
  AddPowerOfficeClient,
  addPowerOfficeClientT,
  getEconomicClientResponseT,
  GetEconomicClientResponse,
  economicCustomerNotFoundErrorT,
  UpdateEconomicClient,
  updateEconomicClientT,
  getBusinessCentralClientResponseT,
  UpdateBusinessCentralClient,
  updateBusinessCentralClientT,
  getPowerOfficeClientResponseT,
  UpdatePowerOfficeClient,
  updatePowerOfficeClientT,
  getTripletexClientResponseT,
  UpdateTripletexClient,
  updateTripletexClientT,
  clientsRemovedT,
} from "dora-contracts";
import { syncCustomers } from "../economics/customers";

export type { Client };

const prefix = "data/clients";

interface State {
  loading: boolean;
  entities: { [key: string]: Client };
  ids: string[];
  isSyncing: boolean;
}
const initialState: State = {
  loading: false,
  entities: {},
  ids: [],
  isSyncing: false,
};

// The server answers 400 { message, code, params } when e-conomic would refuse
// the customer for a reason the user can fix (e.g. the org has no default VAT
// zone / payment terms). Surface that refusal as the rejection payload so the
// dialog can show it; anything else goes through the normal reporting/toast
// path.
export const addEconomicClient = createErrorReportingAsyncThunk<
  void,
  AddEconomicClient,
  { rejectValue: ServerRejection }
>(`${prefix}/add-economic-client`, async (data, thunkApi) => {
  try {
    await axios.post(
      "/api/e-conomic/customers/economic",
      addEconomicClientT.encode(data)
    );
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (rejection) {
      return thunkApi.rejectWithValue(rejection);
    }
    throw err;
  }
});

// Same server contract as add.
export const updateEconomicClient = createErrorReportingAsyncThunk<
  void,
  { clientId: string } & UpdateEconomicClient,
  { rejectValue: ServerRejection }
>(
  `${prefix}/update-economic-client`,
  async ({ clientId, ...data }, thunkApi) => {
    try {
      await axios.put(
        `/api/e-conomic/customers/${clientId}`,
        updateEconomicClientT.encode(data)
      );
    } catch (err) {
      const rejection = serverRejectionMessage(err);
      if (rejection) {
        return thunkApi.rejectWithValue(rejection);
      }
      throw err;
    }
  }
);

// The server answers 404 ECONOMIC_CUSTOMER_NOT_FOUND when the client's customer
// number no longer exists in e-conomic (DORA-465). That is the org's data, not
// a failure to report: it becomes the rejection payload for the dialog to
// show. Anything else goes through the normal reporting/toast path.
export const getEconomicClientForEdit = createErrorReportingAsyncThunk<
  GetEconomicClientResponse,
  string,
  { rejectValue: ServerRejection }
>(`${prefix}/get-economic-client-for-edit`, async (clientId, thunkApi) => {
  try {
    return await axios
      .get(`/api/e-conomic/customers/${clientId}`)
      .then(getData)
      .then(tPromise.decode(getEconomicClientResponseT));
  } catch (err) {
    const notFound = economicCustomerNotFoundError(err);
    if (notFound) {
      return thunkApi.rejectWithValue({
        kind: "code",
        code: notFound.code,
        params: notFound.params,
        message: notFound.message,
      });
    }
    throw err;
  }
});

const economicCustomerNotFoundError = (err: unknown) =>
  isAxiosError(err) &&
  err.response?.status === 404 &&
  economicCustomerNotFoundErrorT.is(err.response.data)
    ? err.response.data
    : null;

export const getCustomerGroups = createErrorReportingAsyncThunk(
  `${prefix}/load-customer-groups`,
  async () => {
    return axios
      .get("/api/e-conomic/customer-groups")
      .then(getData)
      .then(tPromise.decode(customerGroupsT));
  }
);

export const addTripletexClient = createErrorReportingAsyncThunk(
  `${prefix}/add-tripletex-client`,
  async (data: AddTripletexClient) => {
    await axios.post(
      "/api/tripletex/customers",
      addTripletexClientT.encode(data)
    );
  }
);

export const getTripletexClientForEdit = createErrorReportingAsyncThunk(
  `${prefix}/get-tripletex-client-for-edit`,
  async (clientId: string) => {
    return axios
      .get(`/api/tripletex/customers/${clientId}`)
      .then(getData)
      .then(tPromise.decode(getTripletexClientResponseT));
  }
);

export const updateTripletexClient = createErrorReportingAsyncThunk(
  `${prefix}/update-tripletex-client`,
  async ({
    clientId,
    ...data
  }: { clientId: string } & UpdateTripletexClient) => {
    await axios.put(
      `/api/tripletex/customers/${clientId}`,
      updateTripletexClientT.encode(data)
    );
  }
);

export const addBusinessCentralClient = createErrorReportingAsyncThunk(
  `${prefix}/add-business-central-client`,
  async (data: AddBusinessCentralClient) => {
    await axios.post(
      "/api/business-central/customers",
      addBusinessCentralClientT.encode(data)
    );
  }
);

export const getBusinessCentralClientForEdit = createErrorReportingAsyncThunk(
  `${prefix}/get-business-central-client-for-edit`,
  async (clientId: string) => {
    return axios
      .get(`/api/business-central/customers/${clientId}`)
      .then(getData)
      .then(tPromise.decode(getBusinessCentralClientResponseT));
  }
);

export const updateBusinessCentralClient = createErrorReportingAsyncThunk(
  `${prefix}/update-business-central-client`,
  async ({
    clientId,
    ...data
  }: { clientId: string } & UpdateBusinessCentralClient) => {
    await axios.patch(
      `/api/business-central/customers/${clientId}`,
      updateBusinessCentralClientT.encode(data)
    );
  }
);

export const addPowerOfficeClient = createErrorReportingAsyncThunk(
  `${prefix}/add-power-office-client`,
  async (data: AddPowerOfficeClient) => {
    await axios.post(
      "/api/power-office/customers",
      addPowerOfficeClientT.encode(data)
    );
  }
);

export const getPowerOfficeClientForEdit = createErrorReportingAsyncThunk(
  `${prefix}/get-power-office-client-for-edit`,
  async (clientId: string) => {
    return axios
      .get(`/api/power-office/customers/${clientId}`)
      .then(getData)
      .then(tPromise.decode(getPowerOfficeClientResponseT));
  }
);

export const updatePowerOfficeClient = createErrorReportingAsyncThunk(
  `${prefix}/update-power-office-client`,
  async ({
    clientId,
    ...data
  }: { clientId: string } & UpdatePowerOfficeClient) => {
    await axios.patch(
      `/api/power-office/customers/${clientId}`,
      updatePowerOfficeClientT.encode(data)
    );
  }
);

const PAGINATED_CLIENTS_PAGE_SIZE = 500;

// Clears the client set. Dispatched at the start of a paginated fetch so that
// clients removed/deactivated since the last load don't linger in the store.
export const clientsReset = createAction(`${prefix}/reset`);

// Merges one page of clients into the store, as it arrives.
export const clientsPageLoaded = createAction<Client[]>(
  `${prefix}/page-loaded`
);

// Orchestrates a paginated fetch: resets the set, then streams the clients into
// the store one page at a time, so the UI can render them progressively instead
// of waiting for every page. The server only syncs customers with the finance
// system on the first page (skip=0), so we don't trigger a sync per request.
export const getClientsPaginated = createErrorReportingAsyncThunk(
  `${prefix}/get-clients-paginated`,
  async (_arg: void, thunkAPI) => {
    thunkAPI.dispatch(clientsReset());
    for (let skip = 0; ; skip += PAGINATED_CLIENTS_PAGE_SIZE) {
      const { data } = await axios.get("/api/clients/paginated", {
        params: { skip, limit: PAGINATED_CLIENTS_PAGE_SIZE },
      });
      const page = await tPromise.decode(dispatchingClientsT, data);
      thunkAPI.dispatch(clientsPageLoaded(page));
      // A page smaller than the requested limit is necessarily the last one.
      if (page.length < PAGINATED_CLIENTS_PAGE_SIZE) {
        break;
      }
    }
  }
);

export const getClients = createErrorReportingAsyncThunk(
  `${prefix}/get-clients`,
  async (_arg: void, thunkAPI) => {
    const usePagination = selectFeature("paginated-clients-fetch")(
      thunkAPI.getState() as RootState
    );

    if (usePagination) {
      // Delegate to the orchestrator, which streams clients into the store
      // page by page. We still await it so callers that await getClients()
      // (e.g. economics/customers, products) only resolve once everything is
      // loaded. Returning null tells the reducer not to replace the set — the
      // orchestrator has already populated it.
      await thunkAPI.dispatch(getClientsPaginated()).unwrap();
      return null;
    }

    const { data } = await axios.get("/api/clients");
    return tPromise.decode(dispatchingClientsT, data);
  },
  {
    // Dedupe concurrent fetches. Many components dispatch getClients() on
    // mount, and the paginated path starts a multi-request loop per dispatch —
    // without this guard those loops overlap, hammering the same pages and
    // resetting each other's progress. loading stays true for the whole
    // paginated loop (getClients only resolves once the orchestrator finishes),
    // so an in-flight fetch correctly blocks new ones.
    condition: (_arg: void, { getState }) =>
      !(getState() as RootState).data.clients.loading,
  }
);

export const setShowArrivalDepartureToDriverForCustomer =
  ({
    clientId,
    showArrivalDepartureToDriver,
  }: {
    clientId: string;
    showArrivalDepartureToDriver: boolean | null;
  }): AppThunkAction =>
  async (dispatch) => {
    const body = { showArrivalDepartureToDriver };
    const { data } = await axios.put(`/api/clients/${clientId}`, body);
    return dispatch(clientUpdated(data));
  };

export const setGroupSurchargesForCustomer =
  ({
    clientId,
    groupSurcharges,
  }: {
    clientId: string;
    groupSurcharges: boolean;
  }): AppThunkAction =>
  async (dispatch) => {
    const body = { groupSurcharges };
    const { data } = await axios.put(
      `/api/clients/${clientId}/set-group-surcharges`,
      body
    );
    return dispatch(clientUpdated(data));
  };

export const setGroupProductsForCustomer =
  ({
    clientId,
    groupProducts,
  }: {
    clientId: string;
    groupProducts: boolean;
  }): AppThunkAction =>
  async (dispatch) => {
    const body = { groupProducts };
    const { data } = await axios.put(
      `/api/clients/${clientId}/set-group-products`,
      body
    );
    return dispatch(clientUpdated(data));
  };

export const setShowAddCubicMetersToDriverForCustomer =
  ({
    clientId,
    showAddCubicMetersToDriver,
  }: {
    clientId: string;
    showAddCubicMetersToDriver: boolean | null;
  }): AppThunkAction =>
  async (dispatch) => {
    const body = { showAddCubicMetersToDriver };
    const { data } = await axios.put(
      `/api/clients/${clientId}/toggle-show-cubic-meters-to-driver`,
      body
    );
    return dispatch(clientUpdated(data));
  };

export const setVisibleToDrivers =
  ({
    clientId,
    visibleToDrivers,
  }: {
    clientId: string;
    visibleToDrivers: boolean;
  }): AppThunkAction =>
  async (dispatch) => {
    const body = { visibleToDrivers };
    const { data } = await axios.patch(`/api/clients/${clientId}`, body);
    return dispatch(clientUpdated(data));
  };

export const setVisibleToDriversMultiple =
  ({ visibleToDrivers }: { visibleToDrivers: boolean }): AppThunkAction =>
  async (dispatch) => {
    const { data } = await axios.post(
      `/api/clients/set-visible-to-drivers-multiple`,
      { visibleToDrivers }
    );
    return dispatch(clientsUpdated(data));
  };

export const setSurchargesForClient = createErrorReportingAsyncThunk(
  `${prefix}/set-surcharges-for-client`,
  async (input: { clientId: string; surchargesIds: string[] }) => {
    await axios.post(`/api/clients/${input.clientId}/set-surcharges`, input);
  }
);

export const clientCreated = createErrorReportingAsyncThunk(
  `${prefix}/created`,
  async (client: unknown) => {
    return tPromise.decode(dispatchingClientT, client);
  }
);

export const clientUpdated = createErrorReportingAsyncThunk(
  `${prefix}/updated`,
  async (client: unknown) => {
    return tPromise.decode(dispatchingClientT, client);
  }
);

export const clientsRemoved = createErrorReportingAsyncThunk(
  `${prefix}/clients-removed`,
  async (payload: unknown) => {
    return tPromise.decode(clientsRemovedT, payload);
  }
);

export const clientsUpdated = createErrorReportingAsyncThunk(
  `${prefix}/clients-updated`,
  async (clients: unknown) => {
    return tPromise.decode(dispatchingClientsT, clients);
  }
);

export const setWebBookingApiAccess = createErrorReportingAsyncThunk(
  `${prefix}/set-webbooking-api-access`,
  async (input: { clientId: string; value: boolean }, thunkAPI) => {
    const { clientId, value } = input;
    const { data } = await axios.put(
      `/api/clients/${clientId}/toggle-web-booking-api-access`,
      { value }
    );
    return thunkAPI.dispatch(clientUpdated(data));
  }
);

export const setClientAllowWebBookingViewOrdersDeliveredStatus =
  createErrorReportingAsyncThunk(
    `${prefix}/set-webbooking-view-orders-delivered-status`,
    async (input: { clientId: string; value: boolean }, thunkAPI) => {
      const { clientId, value } = input;
      const { data } = await axios.put(
        `/api/clients/${clientId}/toggle-web-booking-view-orders-delivered-status`,
        { value }
      );
      return thunkAPI.dispatch(clientUpdated(data));
    }
  );

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getClients.pending, (state) => {
        state.loading = true;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.loading = false;
        // Paginated fetches return null: the orchestrator has already populated
        // the store via clientsReset / clientsPageLoaded, so don't replace it.
        if (action.payload) {
          state.entities = keyBy(action.payload, "id");
          state.ids = action.payload.map((x) => x.id);
        }
      })
      .addCase(clientsReset, (state) => {
        state.loading = true;
        state.entities = {};
        state.ids = [];
      })
      .addCase(clientsPageLoaded, (state, action) => {
        for (const client of action.payload) {
          if (!state.entities[client.id]) {
            state.ids.push(client.id);
          }
          state.entities[client.id] = client;
        }
      })
      .addCase(clientUpdated.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
        // this also happens when a barred client is unbarred
        if (!state.ids.includes(action.payload.id)) {
          state.ids.push(action.payload.id);
        }
      })
      .addCase(clientsUpdated.fulfilled, (state, action) => {
        for (const client of action.payload) {
          state.entities[client.id] = client;
          if (!state.ids.includes(client.id)) {
            state.ids.push(client.id);
          }
        }
      })
      .addCase(clientCreated.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
        state.ids.push(action.payload.id);
      })
      .addCase(clientsRemoved.fulfilled, (state, action) => {
        // One pass over ids for the whole chunk, not one per removed client.
        const removed = new Set(action.payload.ids);
        for (const id of removed) {
          delete state.entities[id];
        }
        state.ids = state.ids.filter((id) => !removed.has(id));
      })
      .addCase(syncCustomers.pending, (state) => {
        state.isSyncing = true;
      })
      .addCase(syncCustomers.fulfilled, (state) => {
        state.isSyncing = false;
      })
      .addCase(syncCustomers.rejected, (state) => {
        state.isSyncing = false;
      })
      .addCase(setWebBookingApiAccess.fulfilled, (state, action) => {
        state.entities[action.meta.arg.clientId].webBookerAllowApiAccess =
          action.meta.arg.value;
      })
      .addCase(
        setClientAllowWebBookingViewOrdersDeliveredStatus.fulfilled,
        (state, action) => {
          state.entities[
            action.meta.arg.clientId
          ].webBookerCanSeeOrdersDelivered = action.meta.arg.value;
        }
      );
  },
});

export default slice.reducer;
